import { createClient } from '@/lib/supabase/client';
import type { Sale } from '@/lib/database';
import { emptyToNull, newId, orThrow, todayStr, DEFAULT_PAGE_SIZE } from './useEntityList';
import { setDiamondStatus } from './diamonds';

// DB columns match the UI type exactly.
type SaleRow = Sale;

export async function listSales(): Promise<Sale[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('sales')
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .order('sale_date', { ascending: false });
  return orThrow(data, error) as SaleRow[];
}

export async function createSale(
  input: Omit<Sale, 'id' | 'created_at' | 'paid_date'> & { paid_date?: string | null }
): Promise<Sale> {
  const supabase = createClient();
  const payload = emptyToNull(
    {
      ...input,
      paid_date: input.paid_date || null,
      id: newId('sale'),
      created_at: new Date().toISOString(),
    },
    ['customer_id', 'diamond_id']
  );
  const { data, error } = await supabase
    .from('sales')
    .insert(payload)
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  const sale = orThrow(data, error) as SaleRow;
  // Recording a sale takes the stone out of available inventory.
  if (sale.diamond_id) {
    await setDiamondStatus(sale.diamond_id, 'SOLD').catch(() => {});
  }
  return sale;
}

export async function updateSale(
  id: string,
  patch: Partial<Omit<Sale, 'id' | 'created_at'>>
): Promise<Sale> {
  const supabase = createClient();
  const payload = patch.paid_date === '' ? { ...patch, paid_date: null } : patch;
  const { data, error } = await supabase
    .from('sales')
    .update(payload)
    .eq('id', id)
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  return orThrow(data, error) as SaleRow;
}

/** Mark an invoice paid with today's date. */
export async function markSalePaid(id: string): Promise<Sale> {
  return updateSale(id, { payment_status: 'PAID', paid_date: todayStr() });
}
